import { LogOut, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { startGoogleLogin } from "@/lib/auth";

export const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const SignedIn = ({ user, logout }) => (
  <div className="flex items-center gap-2 min-w-0">
    {user.picture ? (
      <img src={user.picture} alt={`${user.name} avatar`} className="h-8 w-8 shrink-0 rounded-full border border-white/10" referrerPolicy="no-referrer" />
    ) : (
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#2563EB] text-xs font-bold">{user.name?.[0] ?? "U"}</span>
    )}
    <span className="hidden md:block max-w-[140px] truncate text-sm text-slate-200" data-testid="auth-user-name">{user.name}</span>
    {user.role === "admin" && (
      <span className="hidden sm:inline-flex items-center gap-1 rounded-full border border-violet-400/40 bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold text-violet-200" data-testid="auth-admin-badge">
        <ShieldCheck className="h-3 w-3" aria-hidden="true" /> Admin
      </span>
    )}
    <button
      type="button"
      onClick={async () => { await logout(); toast.success("Signed out"); }}
      className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-rose-400/40 hover:text-rose-200"
      data-testid="auth-logout-button"
    >
      <LogOut className="h-3.5 w-3.5" aria-hidden="true" /> <span className="hidden sm:inline">Sign out</span>
    </button>
  </div>
);

export const AuthControls = () => {
  const { user, authChecked, logout } = useApp();
  if (!authChecked) return <span className="h-8 w-24 rounded-full bg-white/5 animate-pulse" data-testid="auth-loading" />;
  if (user) return <SignedIn user={user} logout={logout} />;
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => startGoogleLogin()}
        className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5"
        data-testid="auth-google-login-button"
      >
        <GoogleIcon /> <span>Sign in<span className="hidden sm:inline"> with Google</span></span>
      </button>
      <button
        type="button"
        disabled
        title="Sign in with Apple — coming soon"
        className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-500 cursor-not-allowed"
        data-testid="auth-apple-login-button"
      >
         Apple · Soon
      </button>
    </div>
  );
};
